import React, { useRef, useState } from 'react';
import { View, StyleSheet, ScrollView, TouchableOpacity, Text } from 'react-native';
import { IconButton, useTheme } from 'react-native-paper';

interface Tab {
    key: string;
    label: string;
}

interface TabNavigationProps {
    tabs: Tab[];
    activeTab: string;
    onTabChange: (tabKey: string) => void;
    children?: React.ReactNode;
}

export const TabNavigation: React.FC<TabNavigationProps> = ({ 
    tabs, 
    activeTab, 
    onTabChange,
    children 
}) => {
    const theme = useTheme();
    const scrollViewRef = useRef<ScrollView>(null);
    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const [showRightArrow, setShowRightArrow] = useState(true);

    const handleScroll = (event: any) => {
        const { layoutMeasurement, contentOffset, contentSize } = event.nativeEvent;
        setShowLeftArrow(contentOffset.x > 0);
        setShowRightArrow(contentOffset.x < contentSize.width - layoutMeasurement.width - 10);
    };

    const scrollTo = (direction: 'left' | 'right') => {
        if (scrollViewRef.current) {
            const offset = direction === 'left' ? -200 : 200;
            scrollViewRef.current.scrollTo({
                x: offset,
                animated: true
            });
        }
    };

    return (
        <View style={styles.container}>
            <View style={styles.tabContainer}>
                {showLeftArrow && (
                    <TouchableOpacity 
                        style={[
                            styles.arrow, 
                            styles.leftArrow, 
                            { backgroundColor: theme.colors.elevation.level2 }
                        ]}
                        onPress={() => scrollTo('left')}
                    >
                        <IconButton icon="chevron-left" size={16} />
                    </TouchableOpacity>
                )}
                
                <ScrollView
                    ref={scrollViewRef}
                    horizontal
                    showsHorizontalScrollIndicator={false}
                    onScroll={handleScroll}
                    scrollEventThrottle={16}
                    style={styles.scrollView}
                >
                    <View style={styles.tabsRow}>
                        {tabs.map((tab) => (
                            <TouchableOpacity
                                key={tab.key}
                                style={[
                                    styles.tab,
                                    activeTab === tab.key && styles.activeTab,
                                    { borderBottomColor: theme.colors.primary }
                                ]}
                                onPress={() => onTabChange(tab.key)}
                            >
                                <IconButton
                                    icon="file-document-outline"
                                    size={16}
                                    iconColor={activeTab === tab.key ? theme.colors.primary : theme.colors.onBackground}
                                />
                                <Text style={[
                                    styles.tabLabel,
                                    activeTab === tab.key && { color: theme.colors.primary },
                                    { color: activeTab === tab.key ? theme.colors.primary : theme.colors.onBackground }
                                ]}>
                                    {tab.label}
                                </Text>
                            </TouchableOpacity>
                        ))}
                    </View>
                </ScrollView>

                {showRightArrow && (
                    <TouchableOpacity 
                        style={[
                            styles.arrow, 
                            styles.rightArrow, 
                            { backgroundColor: theme.colors.elevation.level2 }
                        ]}
                        onPress={() => scrollTo('right')}
                    >
                        <IconButton icon="chevron-right" size={16} />
                    </TouchableOpacity>
                )}
            </View>
            
            <View style={styles.content}>
                {children}
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
    },
    tabContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        position: 'relative',
        borderBottomWidth: 1,
        borderBottomColor: '#e0e0e0',
    },
    scrollView: {
        flexGrow: 1,
    },
    tabsRow: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    tab: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 4,
        paddingHorizontal: 8,
        minWidth: 'auto',
        gap: 4,
    },
    activeTab: {
        borderBottomWidth: 2,
    },
    tabLabel: {
        fontSize: 13,
        paddingBottom: 2,
    },
    arrow: {
        position: 'absolute',
        zIndex: 1,
        height: '100%',
        justifyContent: 'center',
        alignItems: 'center',
        width: 24,
    },
    leftArrow: {
        left: 0,
    },
    rightArrow: {
        right: 0,
    },
    content: {
        flex: 1,
    },
});
